import React, { FC } from 'react';

import { Box, Flex, HStack, Stack } from '@chakra-ui/react';
import map from 'lodash/map';

import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import {
  DeleteAttributeIconButton,
  FormLabel,
  formLabelStyles,
  PrimaryButton,
  Select,
} from 'components/DataEntry';
import { SnowflakeCondition } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Criteria/Conditions/SnowflakeCondition';
import {
  addConditionButtonStyles,
  snowflakeConditionGridStyles,
} from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/styles';

import { renderDataPolicyConditionLabel } from '../../../utils';

interface Props {
  state: Record<string, any>;
  conditions: Record<string, any>[];
  dataGroupOptions: Record<string, any>[];
  roleOptions: Record<string, any>[];
  selectedDataGroup: Record<string, any>;
  updateConditions: (
    item: Record<string, any>,
    key: string,
    value: any,
  ) => void;
  onClickAddCondition: (
    principalType: Record<string, any>,
    maskingOptions?: Record<string, any>,
    policyTypeOptions?: Record<string, any>,
    dataGroup?: Record<string, any>,
    roleOptions?: Record<string, any>[],
  ) => void;
  onDeleteCondition: (item: Record<string, any>, key: string) => void;
  onDeleteDataGroup: (dataGroup: Record<string, any>) => void;
  onChangeDataGroup: (dataGroup: Record<string, any>) => void;
  isDeleteDisabled?: boolean;
}

export const DataPolicyConditionBlock: FC<Props> = props => {
  const {
    state,
    conditions,
    dataGroupOptions,
    roleOptions,
    selectedDataGroup,
    updateConditions,
    onClickAddCondition,
    onDeleteCondition,
    onDeleteDataGroup,
    onChangeDataGroup,
    isDeleteDisabled,
  } = props;
  const selectedRoleValues = new Set(
    conditions.map(condition => condition?.role?.value).filter(Boolean),
  );
  const availableRoleOptions = roleOptions.filter(
    roleOption => !selectedRoleValues.has(roleOption?.value),
  );

  return (
    <BorderedBox p={0} overflow="hidden">
      <Stack spacing={5} py={5} px={8}>
        <Flex justify="space-between" align="center">
          {renderDataPolicyConditionLabel(
            'FOR',
            'the following target data sources',
            true,
          )}
          <DeleteAttributeIconButton
            onClick={() => onDeleteDataGroup(selectedDataGroup)}
            isDisabled={isDeleteDisabled}
          />
        </Flex>
        <Stack spacing={0}>
          <FormLabel
            htmlFor="data-group"
            label="Data Group"
            styles={formLabelStyles}
          />
          <HStack>
            <Box flex={1} minW={0}>
              <Select
                options={dataGroupOptions}
                value={selectedDataGroup}
                onChange={onChangeDataGroup}
                showClearIndicator={false}
              />
            </Box>
          </HStack>
        </Stack>
        <Flex justify="flex-end">
          <PrimaryButton
            {...addConditionButtonStyles}
            onClick={() =>
              onClickAddCondition(
                state.principalType,
                undefined,
                undefined,
                selectedDataGroup,
                availableRoleOptions,
              )
            }
            isDisabled={!availableRoleOptions.length}
          >
            + ADD ROLE
          </PrimaryButton>
        </Flex>
        <Box {...snowflakeConditionGridStyles}>
          <Box w="full">{renderDataPolicyConditionLabel('IF', 'Role is')}</Box>
          <Box w="full">
            {renderDataPolicyConditionLabel('THEN', 'Column Mask is')}
          </Box>
        </Box>
        <Stack spacing={3}>
          {map(conditions, (condition, index) => (
            <SnowflakeCondition
              key={condition?.id ?? index}
              condition={condition}
              state={state}
              roleOptions={roleOptions.filter(
                roleOption =>
                  roleOption?.value === condition?.role?.value ||
                  !selectedRoleValues.has(roleOption?.value),
              )}
              updateConditions={updateConditions}
              onDeleteCondition={onDeleteCondition}
              isDeleteDisabled={conditions.length <= 1}
            />
          ))}
        </Stack>
      </Stack>
    </BorderedBox>
  );
};
